/**
 * `WebhookIntegrationBlock` — renders one generic-webhook CI integration
 * as a `<ProviderBlock>`.
 *
 * The webhook provider is simpler than GitHub: it has no live GitHub data
 * to fetch, so the block body is lightweight — just the callback URL in
 * the subtitle and a kebab menu with Rotate secret + Delete.
 *
 * Kebab items:
 *   1. Rotate secret  — opens the rotate confirmation dialog.
 *   2. Delete         — opens the destructive delete confirmation.
 *
 * The reveal-once secret banner is managed by the parent
 * (`WorkspaceIntegrations` after the #1153 consolidation; was
 * `WorkspaceCiIntegrations` pre-#1153) so it spans the full provider
 * section. This block merely calls the parent callbacks on rotate/
 * delete completion.
 */
import { useState } from 'react'
import { RotateCw, Trash2, Webhook } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui'
import {
  ProviderBlock,
  ConnectedBadge,
} from '@/components/ProviderBlock'
import {
  ciIntegrationsAPI,
  type CiIntegration,
  type CiIntegrationWithSecret,
} from '@/lib/api'
import { fmtDate } from './ciIntegrationsUtils'
import { useUserTimezone } from '@/hooks/useUserTimezone'

// ── Webhook mark ──────────────────────────────────────────────────────────

/** Plug/webhook icon tile — uses lucide's `Webhook` icon on a neutral
 * muted-foreground background to indicate a generic HTTP integration. */
function WebhookMark() {
  return (
    <div className="w-[38px] h-[38px] rounded-lg bg-muted flex items-center justify-center">
      <Webhook className="w-[20px] h-[20px] text-muted-foreground" aria-hidden="true" />
    </div>
  )
}

// ── Props ─────────────────────────────────────────────────────────────────

interface WebhookIntegrationBlockProps {
  integration: CiIntegration
  onRotated: (result: CiIntegrationWithSecret) => void
  onDeleted: () => void
  onError: (msg: string) => void
}

// ── Component ─────────────────────────────────────────────────────────────

export function WebhookIntegrationBlock({
  integration,
  onRotated,
  onDeleted,
  onError,
}: WebhookIntegrationBlockProps) {
  const tz = useUserTimezone()
  const [rotateOpen, setRotateOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const subtitle = integration.callbackUrl ? (
    <code className="font-mono text-[11px] text-muted-foreground">
      {integration.callbackUrl}
    </code>
  ) : (
    <span className="text-muted-foreground">No callback URL configured</span>
  )

  const menuItems = [
    {
      key: 'rotate',
      label: 'Rotate secret',
      icon: <RotateCw className="h-3.5 w-3.5" />,
      onClick: () => setRotateOpen(true),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-3.5 w-3.5" />,
      danger: true,
      separator: true,
      onClick: () => setDeleteOpen(true),
    },
  ]

  return (
    <>
      <ProviderBlock
        mark={<WebhookMark />}
        name="Generic webhook"
        subtitle={subtitle}
        statusBadge={<ConnectedBadge />}
        menuItems={menuItems}
      >
        <div className="text-xs text-muted-foreground space-y-0.5">
          <div>Last activity: {fmtDate(integration.lastUsedAt, tz)}</div>
          <div>Connected: {fmtDate(integration.createdAt, tz)}</div>
        </div>
      </ProviderBlock>

      <WebhookRotateDialog
        open={rotateOpen}
        integration={integration}
        onClose={() => setRotateOpen(false)}
        onRotated={(result) => {
          setRotateOpen(false)
          onRotated(result)
        }}
        onError={onError}
      />

      <WebhookDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false)
          onDeleted()
        }}
        onError={onError}
        integration={integration}
      />
    </>
  )
}

// ── Rotate secret dialog ──────────────────────────────────────────────────

function WebhookRotateDialog({
  open,
  integration,
  onClose,
  onRotated,
  onError,
}: {
  open: boolean
  integration: CiIntegration
  onClose: () => void
  onRotated: (result: CiIntegrationWithSecret) => void
  onError: (msg: string) => void
}) {
  const [submitting, setSubmitting] = useState(false)

  async function handleRotate() {
    setSubmitting(true)
    try {
      const result = await ciIntegrationsAPI.rotateSecret(integration.id)
      onRotated(result)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to rotate secret')
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rotate webhook secret?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            We'll generate a new signing secret. New deliveries start using it immediately.
          </p>
          <p className="text-sm text-muted-foreground">
            If a delivery is already on its way when you rotate, it still uses the old secret.
            To avoid missing any messages, keep both secrets active on your side for about a day
            before removing the old one.{' '}
            <a
              href="/docs/ci-cd-integration"
              target="_blank"
              rel="noreferrer"
              className="underline hover:no-underline"
            >
              Learn more
            </a>
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>Before clicking Rotate:</strong> add the new secret to your CI alongside
            the old one. After about a day of clean deliveries, you can safely remove the old secret.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleRotate()} disabled={submitting}>
            {submitting ? 'Rotating…' : 'Rotate secret'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Delete dialog ─────────────────────────────────────────────────────────

function WebhookDeleteDialog({
  open,
  onClose,
  onDeleted,
  onError,
  integration,
}: {
  open: boolean
  onClose: () => void
  onDeleted: () => void
  onError: (msg: string) => void
  integration: CiIntegration
}) {
  const [submitting, setSubmitting] = useState(false)

  async function handleDelete() {
    setSubmitting(true)
    try {
      await ciIntegrationsAPI.delete(integration.id)
      onDeleted()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete integration')
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete this webhook integration?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-muted-foreground">
            OttoTester will stop posting to this destination. Deliveries that are already
            in transit may still arrive; no future runs will fire to this URL.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void handleDelete()} disabled={submitting}>
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
